import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { isAdminLoggedIn, adminLogin, adminLogout, sha256, getSettings, DEFAULT_ADMIN_PASSWORD } from '@/lib/store';

interface AdminContextValue {
  isLoggedIn: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
}

const AdminContext = createContext<AdminContextValue | null>(null);

export function AdminProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(() => isAdminLoggedIn());

  const login = useCallback(async (password: string): Promise<boolean> => {
    const settings = getSettings();
    const inputHash = await sha256(password);

    let authorized = false;

    if (settings.adminPasswordHash) {
      // Compare against stored hash
      authorized = inputHash === settings.adminPasswordHash;
    } else {
      // No password set yet — accept the default password and store its hash
      if (password === DEFAULT_ADMIN_PASSWORD) {
        const { saveSettings } = await import('@/lib/store');
        saveSettings({ adminPasswordHash: await sha256(DEFAULT_ADMIN_PASSWORD) });
        authorized = true;
      }
    }

    if (authorized) {
      adminLogin();
      setIsLoggedIn(true);
    }
    return authorized;
  }, []);

  const logout = useCallback(() => {
    adminLogout();
    setIsLoggedIn(false);
  }, []);

  return (
    <AdminContext.Provider value={{ isLoggedIn, login, logout }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}
